import { useEffect, useState, useCallback } from 'react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isManager, isAdmin } from '../utils/permissions';

const PREF_KEY = 'dashboard_scope_site_ids';

// Backend response envelopes are inconsistent across this codebase (see
// MEMORY.md "API response envelopes" gotcha) — getUserSites/getSites may return
// a bare array or { data: [...] }. Same defensive unwrap as ManagedSitesEditor.tsx.
const toArr = (x: any): any[] => (Array.isArray(x) ? x : Array.isArray(x?.data) ? x.data : []);

export interface DashboardScopeSite {
  id: string;
  name: string;
}

export interface DashboardScope {
  allowedSites: DashboardScopeSite[];
  selected: string[];
  setSelected: (ids: string[]) => void;
  siteIdsParam: string;
  hasCeiling: boolean;
}

// Reads the manager's site ceiling (user_sites) and their saved personal filter
// (user_preferences.preferences.dashboard_scope_site_ids). The personal filter is
// a DISPLAY preference only — the server re-clamps site_ids to the ceiling, so
// nothing here can ever widen authorization.
export function useDashboardScope(): DashboardScope {
  const { user } = useAuth();
  const canScope = isManager(user?.role) || isAdmin(user?.role);
  const [allowedSites, setAllowedSites] = useState<DashboardScopeSite[]>([]);
  const [selected, setSelectedState] = useState<string[]>([]);
  const [prefs, setPrefs] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!canScope || !user?.id) return;
    let cancelled = false;
    (async () => {
      const [mineRes, allRes, prefRes] = await Promise.all([
        apiService.getUserSites(user.id),
        apiService.getSites(),
        apiService.getUserPreferences(),
      ]);
      if (cancelled) return;

      const allowedIds = new Set(toArr(mineRes).map((r: any) => r.site_id));
      setAllowedSites(
        toArr(allRes)
          .filter((s: any) => allowedIds.has(s.id))
          .map((s: any) => ({ id: s.id, name: s.name })),
      );

      // Real envelope: { success, data: { preferences: {...} } } — see
      // backend/internal/handlers/rest_handlers.go handleGetUserPreferences.
      const p = (prefRes as any)?.data?.preferences || {};
      setPrefs(p);
      const saved: string[] = Array.isArray(p[PREF_KEY]) ? p[PREF_KEY] : [];
      // Drop any saved id no longer in the ceiling (defensive: ceiling may have shrunk).
      setSelectedState(saved.filter((id) => allowedIds.has(id)));
    })();
    return () => {
      cancelled = true;
    };
  }, [canScope, user?.id]);

  const setSelected = useCallback(
    (ids: string[]) => {
      setSelectedState(ids);
      const next = { ...prefs, [PREF_KEY]: ids };
      setPrefs(next);
      apiService.updateUserPreferences({ preferences: next }).catch((err) => {
        // Non-fatal: the selection is already applied in-memory for this session;
        // surface the failure rather than swallowing it so a broken persist is
        // diagnosable instead of silently forgetting the manager's filter.
        // eslint-disable-next-line no-console
        console.error('Failed to persist dashboard scope filter', err);
      });
    },
    [prefs],
  );

  return {
    allowedSites,
    selected,
    setSelected,
    siteIdsParam: selected.join(','),
    hasCeiling: allowedSites.length > 0,
  };
}
